import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import {
  ArrowRight,
  Code2,
  Globe,
  Link2,
  RefreshCcw,
  Search,
  GraduationCap,
  CheckCircle2,
  Star,
  TrendingUp,
  Users,
  Award,
  Zap,
  Smartphone,
  Wifi,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HomePage() {
  const services = [
    {
      icon: Code2,
      title: "Modules Odoo sur mesure",
      description:
        "Développement de modules métier 100 % sur mesure : workflows, vues, rapports QWeb, automatisations. Du cahier des charges à la mise en production.",
    },
    {
      icon: Globe,
      title: "Sites web & portails Odoo",
      description:
        "Sites vitrines, e-commerce et portails client bâtis sur Odoo Website — rapides, SEO-ready, connectés à votre back-office en temps réel.",
    },
    {
      icon: Link2,
      title: "Intégrations API",
      description:
        "Connecteurs entre Odoo et vos outils tiers : e-commerce, ERP, comptabilité, logistique, CRM… via API REST, webhooks ou EDI.",
    },
    {
      icon: RefreshCcw,
      title: "Migration Odoo",
      description:
        "Migration de votre instance vers Odoo 18 ou 19 : audit des modules, migration des données, tests de non-régression, go-live accompagné.",
    },
    {
      icon: Search,
      title: "Audit & Conseil Odoo",
      description:
        "Analyse de votre instance existante : performance, sécurité, qualité du code, bonnes pratiques. Recommandations actionnables.",
    },
    {
      icon: GraduationCap,
      title: "Formation & Maintenance",
      description:
        "Formations utilisateurs et administrateurs, contrats de maintenance mensuelle, support réactif et montées de version assurées.",
    },
    {
      icon: Smartphone,
      title: "Applications mobiles & PWA",
      description:
        "Applications iOS, Android et web mobile pour vos clients ou vos équipes terrain, connectées à Odoo et utilisables même hors connexion.",
    },
    {
      icon: Wifi,
      title: "Solutions IoT & terrain",
      description:
        "Suivi d’équipements, alertes, photos, géolocalisation, scan et signature : les données du terrain remontent directement dans Odoo.",
    },
  ];

  const stats = [
    { value: "40", label: "Modules Odoo développés", icon: TrendingUp },
    { value: "12", label: "Sociétés équipées", icon: Users },
    { value: "18 & 19", label: "Versions Odoo en production", icon: Star },
    { value: "Depuis 2020", label: "D'expérience Odoo", icon: Award },
  ];


  const features = [
    "Expertise Odoo Community & Enterprise",
    "Code propre, documenté, maintenable",
    "Méthodologie agile et transparente",
    "Reprise de données et migration assurées",
    "Déploiement Odoo.sh ou on-premise",
    "Applications iOS, Android & PWA connectées",
    "Formation, support et maintenance inclus",
  ];

  const testimonials = [];  // en attente de vrais retours clients


  const process = [
    { step: "01", title: "Découverte", desc: "On échange sur votre contexte, votre Odoo existant et vos objectifs." },
    { step: "02", title: "Cadrage", desc: "Analyse technique, spécifications fonctionnelles, estimation chiffrée." },
    { step: "03", title: "Développement", desc: "Sprints courts, démos régulières, branches Git, code reviewé." },
    { step: "04", title: "Livraison", desc: "Tests de recette, déploiement, formation et documentation inclus." },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-100/50 rounded-full blur-3xl" />

        <div className="nova-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="nova-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Experts Odoo, web & mobile · La Réunion & remote
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 nova-fade-in-delay-1"
              data-testid="hero-title"
            >
              Votre écosystème métier, du back-office à
              l’<span className="nova-gradient-text">app mobile</span>
            </h1>

            <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto nova-fade-in-delay-2">
              NovaForge conçoit vos modules Odoo, applications iOS et Android,
              portails web et intégrations API — un seul écosystème, du cahier
              des charges à la mise en production.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center nova-fade-in-delay-3">
              <Link to="/onboarding" data-testid="hero-cta-primary">
                <Button className="nova-btn-primary text-base px-10 py-4 h-auto">
                  Décrire mon projet
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-secondary">
                <Button className="nova-btn-secondary text-base px-10 py-4 h-auto">
                  Voir nos réalisations
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="nova-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.length > 0 && stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-xl mb-4">
                  <stat.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="nova-section bg-slate-50" data-testid="services-section">
        <div className="nova-container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Nos expertises numériques</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Odoo reste le cœur de votre système. Nous l’étendons avec le web,
              le mobile et les objets connectés pour couvrir tout le cycle de vos opérations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="nova-card group cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-indigo-600 transition-colors">
                  <service.icon className="w-7 h-7 text-indigo-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="nova-btn-secondary">
                Découvrir tous nos services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Notre processus */}
      <section className="nova-section bg-white">
        <div className="nova-container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Un processus clair, de bout en bout
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Chaque projet démarre par une vraie session de découverte.
              Pas de devis au doigt mouillé, pas de mauvaises surprises.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, i) => (
              <div key={i} className="relative">
                <span className="text-5xl font-bold text-indigo-100 block mb-3">{item.step}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="nova-section bg-slate-50">
        <div className="nova-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Pourquoi choisir{" "}
                <span className="nova-gradient-text">NovaForge</span> ?
              </h2>
              <p className="text-slate-600 mb-8">
                Odoo reste le cœur de notre expertise. Nous l’étendons avec des
                applications web et mobiles connectées, sans multiplier les
                systèmes ni les doubles saisies.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/onboarding">
                  <Button className="nova-btn-primary">
                    Parlez-nous de votre projet
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-indigo-100 rounded-3xl -rotate-3" />
              <img
                src="https://images.unsplash.com/photo-1554306274-f23873d9a26c?w=800&q=80"
                alt="Code Odoo sur écran"
                className="relative rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="nova-section bg-gradient-to-br from-indigo-600 to-indigo-700">
        <div className="nova-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à démarrer votre projet Odoo ou mobile ?
          </h2>
          <p className="text-indigo-100 mb-10 max-w-2xl mx-auto">
            Remplissez notre formulaire de cadrage en 5 minutes. On vous
            recontacte sous 24h avec une première analyse.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/onboarding" data-testid="cta-start-project">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50 rounded-full px-10 py-4 h-auto text-base font-semibold">
                Décrire mon projet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="cta-contact">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-4 h-auto text-base">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
